// ============================================================
// INVOICE TABLE ROW COMPONENT
// ============================================================

import { ExternalLink, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { DeleteInvoiceModal } from '../../../components/invoices/DeleteInvoiceModal';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { api } from '../../../services/api';
import type { Invoice } from '../../../types/invoices.types';

interface InvoiceTableRowProps {
  invoice: Invoice;
  onDelete?: () => void;
}

export function InvoiceTableRow({ invoice, onDelete }: InvoiceTableRowProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      PAID: 'Payée',
      PENDING: 'En attente',
      CANCELLED: 'Annulée',
    };
    return labels[status] || status;
  };

  const getStatusBadgeClasses = (status: string): string => {
    return cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase border border-white/20 whitespace-nowrap',
      status === 'PAID' && 'bg-emerald-100 text-emerald-700',
      status === 'PENDING' && 'bg-amber-100 text-amber-700',
      status === 'CANCELLED' && 'bg-slate-100 text-slate-700',
    );
  };

  // Déterminer si c'est une échéance mensuelle
  const isMonthlyInstallment = !!invoice.installmentInfo;

  // Vérifier si la facture a un lien Drive
  const hasDriveLink = invoice.pdfUrl && invoice.pdfUrl.includes('drive.google.com');

  const handleOpenInvoice = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasDriveLink) {
      window.open(invoice.pdfUrl, '_blank');
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    const loadingToast = toast.loading('Suppression de la facture en cours...');

    try {
      await api.delete(`/invoices/admin/${invoice.id}`);

      toast.dismiss(loadingToast);
      toast.success('Facture supprimée', {
        description: `La facture ${invoice.reference} a été supprimée avec succès.`,
      });

      setShowDeleteModal(false);
      onDelete?.();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);

      toast.dismiss(loadingToast);
      toast.error('Erreur de suppression', {
        description: error.response?.data?.error || 'Impossible de supprimer la facture. Veuillez réessayer.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <tr className="hover:bg-slate-50/50 transition-colors">
      <td className="px-5 py-4 pl-6">
        <div className="flex flex-col gap-1">
          <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">{invoice.reference}</code>
          {isMonthlyInstallment && (
            <span className="text-xs text-blue-600 font-medium">
              Mois {invoice.installmentInfo?.monthNumber || 0}/{invoice.installmentInfo?.totalMonths || 0}
            </span>
          )}
        </div>
      </td>
      <td className="px-5 py-4">
        <div>
          <p className="text-sm font-medium text-slate-900">{invoice.customerName}</p>
          <p className="text-xs text-slate-500">{invoice.customerEmail}</p>
        </div>
      </td>
      <td className="px-5 py-4">
        <span className="text-sm text-slate-700">{invoice.spaceType?.name || 'N/A'}</span>
      </td>
      <td className="px-5 py-4">
        <span className="text-sm text-slate-700">
          {invoice.paidAt || invoice.date
            ? new Date(invoice.paidAt || invoice.date).toLocaleDateString('fr-FR')
            : 'N/A'}
        </span>
      </td>
      <td className="px-5 py-4">
        <span className="text-sm font-semibold text-slate-900">
          {(invoice.amount ?? 0).toLocaleString('fr-FR')} {invoice.currency}
        </span>
      </td>
      <td className="px-5 py-4">
        <span className={getStatusBadgeClasses(invoice.status)}>{getStatusLabel(invoice.status)}</span>
      </td>
      <td className="px-5 py-4 pr-6 text-right">
        <div className="flex items-center justify-end gap-2">
          {hasDriveLink && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleOpenInvoice}
              className="hover:bg-slate-100"
              title="Ouvrir dans Google Drive"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            className="hover:bg-red-50 hover:text-red-600 transition-colors"
            title="Supprimer la facture"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>

      <DeleteInvoiceModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        invoiceReference={invoice.reference}
        isDeleting={isDeleting}
      />
    </tr>
  );
}
